/**
 * Ad manager (ads epic — sc-6958/6959). The single brain between call sites
 * (ad-slot.ts, cabinet-ad.ts, play.ts) and the providers.
 *
 * Owns three things no provider should:
 *   1. THE PAYING-USER GATE. Entitled players (active pass) see ZERO ads —
 *      no banner, no interstitial, no rewarded prompt. One check, from the
 *      same source of truth as play-access (getWallet().entitlement). This
 *      is a product promise (an unlimited pass is ad-free), so it lives in
 *      exactly one place and every path routes through it.
 *   2. PROVIDER SELECTION. Try providers in order per format; first one that
 *      serves wins. Network (when live) leads for video; house backs banners.
 *   3. THE REWARDED → TOKEN GRANT. A completed rewarded video calls the
 *      server (recordRewardedGrant), which re-caps and credits — the client
 *      never mints a token itself; the server is authoritative.
 *
 * Entitlement is fetched once and cached for the session. It's the same
 * wallet the gate already reads, so this piggybacks rather than double-
 * hammering the API. `refreshEntitlement()` re-reads after a purchase.
 */

import { isSignedIn } from '@arcraz/arcadia-auth-web';
import { getWallet, recordAdEvent, recordRewardedGrant } from '../api/client.js';
import { getOrCreateDeviceId } from '../runtime/identity.js';
import { AD_CONFIG } from './config.js';
import { HouseAdProvider } from './house-provider.js';
import { NetworkAdProvider } from './network-provider.js';
import type {
  AdContext,
  AdProvider,
  BannerHandle,
} from './provider.js';

/** Result of a rewarded attempt, surfaced to the caller (play.ts). */
export interface RewardedOutcome {
  /** True only when a video completed AND the server credited a token. */
  granted: boolean;
  /** Tokens remaining after the grant, when the server reported it. */
  remaining?: number;
  /**
   * Why nothing was granted (for UI copy): 'no-ad' (no fill / unavailable),
   * 'dismissed' (closed early), 'cap' (daily rewarded cap hit), 'entitled'
   * (pass holder — never prompted), 'signin' (anonymous — must sign in to
   * earn tokens; gated BEFORE any video plays), or 'error' (grant call failed).
   */
  reason?: 'no-ad' | 'dismissed' | 'cap' | 'entitled' | 'signin' | 'error';
}

export class AdManager {
  private readonly providers: AdProvider[];
  /** null = unknown (not yet fetched); false = not entitled; true = entitled. */
  private entitled: boolean | null = null;
  private entitlementPromise: Promise<boolean> | null = null;

  constructor(providers?: AdProvider[]) {
    // Network first (leads video + can pre-empt banner when live), house last
    // as the always-available banner backstop.
    this.providers = providers ?? [
      new NetworkAdProvider(AD_CONFIG.network),
      new HouseAdProvider(),
    ];
  }

  /**
   * Resolve entitlement once per session (cached). Fail-open toward NON-
   * entitled: if the wallet read throws we treat the player as not-entitled
   * so ads still show — the money gate is server-side elsewhere; showing an
   * ad to someone whose pass we couldn't confirm is a far smaller sin than
   * hiding ads from everyone on a transient blip. `refreshEntitlement` after
   * a purchase flips this the other way (re-read, then suppress ads).
   */
  private async resolveEntitled(): Promise<boolean> {
    if (this.entitled !== null) return this.entitled;
    if (!this.entitlementPromise) {
      this.entitlementPromise = getWallet()
        .then((w) => {
          this.entitled = w.entitlement !== null;
          return this.entitled;
        })
        .catch(() => {
          this.entitled = false; // fail-open: unknown pass ⇒ show ads
          return false;
        });
    }
    return this.entitlementPromise;
  }

  /** Re-read entitlement (call after a purchase return). */
  async refreshEntitlement(): Promise<void> {
    this.entitled = null;
    this.entitlementPromise = null;
    await this.resolveEntitled();
  }

  /** Synchronous best-effort read for hot paths; null until first resolve. */
  isEntitledCached(): boolean | null {
    return this.entitled;
  }

  /**
   * True when rewarded is enabled AND at least one provider can serve it
   * right now — the paywall uses this to decide whether to render the
   * "Watch an ad" button at all (no dead button when there's no inventory).
   * Does NOT check entitlement/sign-in — those are per-offer gates in
   * offerRewarded; this is just "is there a rewarded ad to be had".
   */
  isRewardedReady(): boolean {
    if (!AD_CONFIG.enabled || !AD_CONFIG.enabledFormats.has('rewarded')) return false;
    return this.providers.some((p) => p.isReady('rewarded'));
  }

  private ctx(slot: string, cartridgeId: string | null): AdContext {
    return { slot, cartridgeId, deviceId: getOrCreateDeviceId() };
  }

  /**
   * Mount a banner for `cartridgeId` into `host`. Returns null when: ads are
   * off, the player is entitled, the cartridge isn't in the banner set, or no
   * provider had a banner. Caller ticks/disposes the returned handle.
   *
   * Kicks the entitlement read in the background; if it resolves "entitled"
   * after mount, the caller's next lifecycle (or refreshEntitlement) tears it
   * down. At mount time we honor the cached value — false/null both allow the
   * banner (fail-open), true suppresses it.
   */
  mountBanner(host: HTMLElement, slot: string, cartridgeId: string | null): BannerHandle | null {
    if (!AD_CONFIG.enabled || !AD_CONFIG.enabledFormats.has('banner')) return null;
    if (cartridgeId !== null && !AD_CONFIG.bannerCartridges.has(cartridgeId)) return null;
    void this.resolveEntitled();
    if (this.entitled === true) return null; // confirmed pass ⇒ no ads
    for (const p of this.providers) {
      const handle = p.mountBanner(host, this.ctx(slot, cartridgeId));
      if (handle) return handle;
    }
    return null;
  }

  /**
   * Show an interstitial (e.g. on gameover). Silently no-ops for entitled
   * players and when no provider has fill. Never throws — an ad failure is
   * never the player's problem.
   */
  async showInterstitial(slot: string, cartridgeId: string | null): Promise<{ upgrade: boolean }> {
    if (!AD_CONFIG.enabled || !AD_CONFIG.enabledFormats.has('interstitial')) return { upgrade: false };
    if (await this.resolveEntitled()) return { upgrade: false }; // pass ⇒ no ads
    const ctx = this.ctx(slot, cartridgeId);
    for (const p of this.providers) {
      if (!p.isReady('interstitial')) continue;
      try {
        const r = await p.showInterstitial(ctx);
        // Viewer tapped "Remove ads" — tell the caller to open the paywall.
        if (r.status === 'upgrade') return { upgrade: true };
        if (r.status === 'shown') return { upgrade: false };
      } catch {
        /* try the next provider */
      }
    }
    return { upgrade: false };
  }

  /**
   * Offer a rewarded video → token. Returns the outcome so the caller can
   * update wallet UI. Entitled players are never prompted ('entitled'). A
   * token is credited ONLY when a video completes; the server re-caps and is
   * the sole authority on whether the token actually lands.
   */
  async offerRewarded(slot: string, cartridgeId: string | null): Promise<RewardedOutcome> {
    const ctx = this.ctx(slot, cartridgeId);

    // Record a 'denied' funnel event (with reason) and return the outcome.
    // 'dismissed'/'error' are NOT denials — a skip is logged by the video
    // player, and 'error' is a grant-call failure, not a refusal to serve.
    const deny = (reason: 'no-ad' | 'signin' | 'entitled' | 'cap'): RewardedOutcome => {
      recordAdEvent({
        slot, adId: 'rewarded', event: 'denied', format: 'rewarded',
        reason, cartridgeId: cartridgeId ?? undefined, deviceId: ctx.deviceId,
      });
      return { granted: false, reason };
    };

    if (!AD_CONFIG.enabled || !AD_CONFIG.enabledFormats.has('rewarded')) {
      return deny('no-ad');
    }
    // Anonymous users can't be granted a token (the server grant requires a
    // player JWT). Gate BEFORE playing a video — never make an anon watch a
    // full clip only to have the grant 401. The UI shows "sign in to earn".
    // The 'signin' denial doubles as a conversion signal in telemetry.
    if (!isSignedIn()) return deny('signin');
    if (await this.resolveEntitled()) return deny('entitled');

    for (const p of this.providers) {
      if (!p.isReady('rewarded')) continue;
      let completed: { adUnit?: string } | null = null;
      try {
        const r = await p.showRewarded(ctx);
        if (r.status === 'completed') completed = { adUnit: r.adUnit };
        else if (r.status === 'dismissed') return { granted: false, reason: 'dismissed' };
        // 'unavailable' ⇒ fall through to the next provider
      } catch {
        continue;
      }
      if (completed) {
        // Video ran to completion — ask the SERVER to grant. It re-caps and
        // decides; we never mint a token client-side.
        try {
          const res = await recordRewardedGrant({
            provider: p.id,
            adUnit: completed.adUnit,
            deviceId: ctx.deviceId,
          });
          if (res.granted) return { granted: true, remaining: res.remaining };
          // Server refused — a cap hit is a real denial worth recording; any
          // other server 'no' is a soft no-ad (also recorded).
          return deny(res.reason === 'reward_cap' ? 'cap' : 'no-ad');
        } catch {
          return { granted: false, reason: 'error' };
        }
      }
    }
    return deny('no-ad');
  }
}

/** Process-wide singleton — one entitlement cache for the whole cabinet. */
let singleton: AdManager | null = null;
export function getAdManager(): AdManager {
  if (!singleton) singleton = new AdManager();
  return singleton;
}
